/**
 * @file Core IndexedDB utilities with version-based approach for upgrades
 * @copyright Generic code designed and created by Kyley Harris
 * @license This is generic utility code not unique to the AGWA project.
 * Created and maintained by Kyley Harris with usage rights granted to all projects.
 *
 * This module provides a robust foundation for working with IndexedDB by implementing:
 *
 * 1. A version-based migration system
 *    - Automatic schema upgrades based on version numbers
 *    - Safe handling of database upgrades with transaction management
 *    - Support for complex migration paths across multiple versions
 *
 * 2. Promise-based wrapper around the callback-heavy IndexedDB API
 *    - All operations return Promises for easier async/await usage
 *    - Error handling is standardized across all operations
 *
 * 3. Schema definition and manipulation utilities
 *    - Helper functions for creating and updating object stores
 *    - Index management with support for compound keys
 *    - Typed operations to work with specific data models
 *
 * 4. Connection lifecycle management
 *    - Proper handling of blocked connections
 *    - Version change notifications
 *    - Graceful error recovery
 */

// Copyright - Kyley Harris (2021) - All rights to copy and reuse allowed
// a version based approach to upgrading a database

/**
 * Options for querying data from an object store or index.
 *
 * @property indexName - Optional name of the index to use for the query
 * @property query - The key or key range to search for
 * @property direction - Direction for cursor iteration (next, prev, nextunique, prevunique)
 */
export interface GetOptions {
  indexName?: string;
  query?: IDBValidKey | IDBKeyRange | null;
  direction?: IDBCursorDirection;
}

/**
 * Debug flag to enable/disable logging. Set to true during development
 * to get detailed logs about database operations.
 */
const debug = false;

/**
 * Logging function that only outputs when debug is enabled.
 * Used throughout the module to provide insights into IndexedDB operations.
 *
 * @param s - The message to log
 */
export const log = (s: string) => {
  if (debug) console.log(s);
};

/**
 * Defines a single database upgrade step.
 * Each upgrade represents a specific database version and contains
 * the migration logic to update from the previous version.
 *
 * @property version - The database version number this upgrade applies to
 * @property execute - Function containing the schema changes to apply during upgrade
 */
export interface DatabaseUpgrade {
  version: number;
  execute: (db: IDBDatabase, tran: IDBTransaction) => void;
}

/**
 * Configuration options for opening a database connection with upgrade handling.
 *
 * @property dbName - Name of the database to open
 * @property version - Current version number of the database
 * @property upgrades - Array of upgrade steps to apply if needed
 * @property onBlocked - Callback function to handle blocked connection events
 */
export interface DatabaseOptions {
  dbName: string;
  version: number;
  upgrades?: DatabaseUpgrade[];
  onBlocked: () => void;
}

/**
 * Options for creating or updating an index on an object store.
 *
 * @property name - Name of the index
 * @property keyPath - Property or properties to index
 * @property options - Index configuration (unique, multiEntry)
 * @property delete - If true, will delete this index instead of creating it
 */
export interface CreateIndexOptions {
  name: string;
  keyPath: string | string[];
  options: IDBIndexParameters;
  delete?: boolean;
}

/**
 * Options for creating an object store with its indexes.
 *
 * @property name - Name of the object store
 * @property indexes - Array of indexes to create on this store
 */
export interface CreateObjectStoreOptions {
  name: string;
  indexes?: CreateIndexOptions[];
}

/** ----------------------------- Small helpers ------------------------------ */

/**
 * Converts an IDBRequest into a Promise.
 * This is a core utility that transforms the event-based IndexedDB API
 * into a more modern Promise-based interface.
 *
 * @template T - The type of the result value
 * @param request - The IDBRequest to convert to a Promise
 * @returns Promise that resolves with the request result or rejects with an error
 */
function requestToPromise<T = unknown>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error('IndexedDB request error'));
  });
}

/**
 * Converts an IDBTransaction's completion into a Promise.
 * This allows waiting for all operations in a transaction to complete.
 *
 * @param tr - The transaction to monitor
 * @returns Promise that resolves when the transaction completes or rejects on error/abort
 */
function transactionDone(tr: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tr.oncomplete = () => resolve();
    tr.onerror = () =>
      reject(tr.error ?? new Error('IndexedDB transaction error'));
    tr.onabort = () =>
      reject(tr.error ?? new Error('IndexedDB transaction aborted'));
  });
}

/** ------------------------- Schema helpers (migrations) -------------------- */

/**
 * Creates a new object store configured for storing objects with an 'id' field as the key.
 * This function is designed to be used within database upgrade functions.
 *
 * Key features:
 * - Sets up the store with 'id' as the keyPath (no auto-increment)
 * - Creates all specified indexes
 * - Handles duplicate index creation gracefully for reentrant upgrades
 *
 * @param db - The database being upgraded
 * @param options - Configuration options for the new object store
 * @returns The newly created IDBObjectStore instance
 */
export function newDbItemObjectStore(
  db: IDBDatabase,
  options: CreateObjectStoreOptions
): IDBObjectStore {
  const store = db.createObjectStore(options.name, {
    keyPath: 'id',
    autoIncrement: false,
  });
  options.indexes?.forEach((option) => {
    if (!option.delete) {
      // guard against duplicate index creation in reentrant upgrades
      if (!store.indexNames.contains(option.name)) {
        store.createIndex(option.name, option.keyPath, option.options);
      }
    }
  });
  return store;
}

/**
 * Updates an existing object store by adding or removing indexes.
 * This function is designed to be used within database upgrade functions.
 *
 * Logic:
 * - Gets a reference to the existing store in the current transaction
 * - For each index in the options:
 *   - If delete flag is set, removes the index if it exists
 *   - Otherwise creates the index if it doesn't already exist
 *
 * @param tran - The active upgrade transaction
 * @param options - Configuration options with indexes to create or delete
 * @returns The updated IDBObjectStore instance
 */
export function updateDbItemObjectStore(
  tran: IDBTransaction,
  options: CreateObjectStoreOptions
): IDBObjectStore {
  const store = tran.objectStore(options.name);
  options.indexes?.forEach((option) => {
    if (!option.delete) {
      if (!store.indexNames.contains(option.name)) {
        store.createIndex(option.name, option.keyPath, option.options);
      }
    } else if (store.indexNames.contains(option.name)) {
      store.deleteIndex(option.name);
    }
  });
  return store;
}

/** ---------------------------- Open / Close DB ----------------------------- */

/**
 * Opens an IndexedDB database with version upgrade handling.
 * This is the core function that manages database creation and schema migrations.
 *
 * The function handles:
 * 1. Opening the database with the specified version
 * 2. Applying sequential upgrades when version changes
 * 3. Managing blocked connection events
 * 4. Error handling during open and upgrade
 *
 * Upgrade Logic:
 * - Only upgrades with version numbers greater than the current database version are applied
 * - Upgrades are sorted by version number to ensure they're applied in the correct order
 * - Each upgrade is executed with access to the database and transaction objects
 *
 * @param options - Database configuration including name, version, and upgrade handlers
 * @returns Promise resolving to an open database connection
 */
export function openDb(options: DatabaseOptions): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = window.indexedDB.open(options.dbName, options.version);

    request.onerror = () => {
      log(`Database Connection Error ${options.dbName}`);
      reject(request.error ?? new Error('Failed to open IndexedDB'));
    };

    request.onblocked = () => {
      log(`Database Pending until unblocked ${options.dbName}`);
      options.onBlocked && options.onBlocked();
    };

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const openReq = event.target as IDBOpenDBRequest;
      const db = openReq.result;
      const tran = openReq.transaction!;
      const oldVersion = event.oldVersion;

      // Ensure deterministic, ordered upgrades
      const upgrades = (options.upgrades || [])
        .filter((u) => u.version > oldVersion)
        .sort((a, b) => a.version - b.version);

      try {
        for (const u of upgrades) {
          u.execute(db, tran);
        }
      } catch (e) {
        // Throwing inside onupgradeneeded will abort the versionchange transaction
        log(`Upgrade error on ${options.dbName}: ${(e as Error).message}`);
        throw e;
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      log(`Database Connection Open ${options.dbName}`);

      // If another tab attempts to upgrade, close gracefully to unblock it
      db.onversionchange = () => {
        log(`Database version change detected, closing ${options.dbName}`);
        try {
          db.close();
        } catch {
          /* noop */
        }
      };

      db.onclose = () => log(`Database Connection Closed ${options.dbName}`);
      resolve(db);
    };
  });
}

/**
 * Safely closes an IndexedDB database connection.
 *
 * @param conn - The database connection to close
 * @returns Promise that resolves when the connection is closed
 */
export function closeDb(conn: IDBDatabase): Promise<void> {
  return new Promise<void>((resolve) => {
    try {
      conn.close();
    } finally {
      resolve();
    }
  });
}

/** ------------------------------- Queries ---------------------------------- */

/**
 * Fetches all items from an object store or index using a cursor.
 * This is a low-level function that provides the foundation for higher-level
 * operations like get() and getAll().
 *
 * @template T - The type of items to retrieve
 * @param objectStoreOrIndex - The object store or index to query
 * @param options - Optional query parameters (key range, direction)
 * @returns Promise resolving to an array of all matching items
 */
export function fetchCursor<T>(
  objectStoreOrIndex: IDBObjectStore | IDBIndex,
  options?: {
    query?: IDBValidKey | IDBKeyRange | null;
    direction?: IDBCursorDirection;
  }
): Promise<T[]> {
  return new Promise<T[]>((resolve, reject) => {
    const results: T[] = [];
    const req = objectStoreOrIndex.openCursor(
      options?.query ?? null,
      options?.direction
    );

    req.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        results.push(cursor.value as T);
        cursor.continue();
      } else {
        resolve(results);
      }
    };
    req.onerror = () => reject(req.error ?? new Error('Cursor request failed'));
  });
}

/**
 * Deletes all data from an object store.
 * This is a destructive operation that clears the entire store.
 *
 * @param conn - The database connection
 * @param storeName - Name of the object store to clear
 * @returns Promise that resolves when the store has been cleared
 */
export function deleteAll(conn: IDBDatabase, storeName: string): Promise<void> {
  const tran = conn.transaction(storeName, 'readwrite');
  const store = tran.objectStore(storeName);
  store.clear();
  return transactionDone(tran).then(() => {
    log(`Cleared all from ${storeName}`);
  });
}

/**
 * Retrieves the first item matching the provided options.
 * This is a commonly used operation for finding a single item by ID or other criteria.
 *
 * @template T - The type of item to retrieve
 * @param conn - The database connection
 * @param storeName - Name of the object store to query
 * @param options - Optional query parameters (index, key/range, direction)
 * @returns Promise resolving to the first matching item or null if none found
 */
export async function get<T>(
  conn: IDBDatabase,
  storeName: string,
  options?: GetOptions
): Promise<T | null> {
  const tran = conn.transaction(storeName, 'readonly');
  const store = tran.objectStore(storeName);
  const source: IDBObjectStore | IDBIndex = options?.indexName
    ? store.index(options.indexName)
    : store;

  // Collect during transaction
  const result = await fetchCursor<T>(source, {
    query: options?.query ?? null,
    direction: options?.direction,
  });

  // Resolve after transaction is complete to surface errors cleanly
  await transactionDone(tran);
  log(`transaction complete on ${storeName}`);
  return result.length > 0 ? result[0] : null;
}

/**
 * Stores a single item in an object store.
 * This function handles both insert and update operations based on the item's ID.
 *
 * @template T - The type of item to store, must include an optional id property
 * @param conn - The database connection
 * @param storeName - Name of the object store
 * @param item - The item to store
 * @returns Promise resolving to the key of the stored item
 */
export function put<T extends { id?: IDBValidKey }>(
  conn: IDBDatabase,
  storeName: string,
  item: T
): Promise<IDBValidKey> {
  const tran = conn.transaction(storeName, 'readwrite');
  const store = tran.objectStore(storeName);
  const req = store.put(item);
  return Promise.all([
    requestToPromise<IDBValidKey>(req),
    transactionDone(tran),
  ]).then(([key]) => {
    log(`transaction complete on ${storeName}`);
    return key;
  });
}

/**
 * Stores multiple items in an object store in a single transaction.
 * This is more efficient than calling put() multiple times for batch operations.
 *
 * Implementation uses a recursive approach to add items one by one within
 * a single transaction, maintaining performance while ensuring data consistency.
 *
 * @template T - The type of items to store
 * @param conn - The database connection
 * @param storeName - Name of the object store
 * @param items - Array of items to store
 * @returns Promise resolving when all items have been stored
 */
export function putAll<T>(
  conn: IDBDatabase,
  storeName: string,
  items: T[]
): Promise<void> {
  const tran = conn.transaction(storeName, 'readwrite');
  const store = tran.objectStore(storeName);

  return new Promise<void>((resolve, reject) => {
    let i = 0;

    const next = () => {
      if (i >= items.length) return;
      const req = store.put(items[i++]);
      req.onsuccess = next;
      req.onerror = () => reject(req.error ?? new Error('putAll failed'));
    };

    tran.oncomplete = () => resolve();
    tran.onerror = () =>
      reject(tran.error ?? new Error('putAll transaction error'));
    tran.onabort = () =>
      reject(tran.error ?? new Error('putAll transaction aborted'));

    next();
  });
}

/**
 * Retrieves all items matching the provided options from an object store.
 *
 * @template T - The type of items to retrieve
 * @param conn - The database connection
 * @param storeName - Name of the object store to query
 * @param options - Optional query parameters (index, key/range, direction)
 * @returns Promise resolving to an array of all matching items
 */
export async function getAll<T>(
  conn: IDBDatabase,
  storeName: string,
  options?: GetOptions
): Promise<T[]> {
  const tran = conn.transaction(storeName, 'readonly');
  const store = tran.objectStore(storeName);
  const source: IDBObjectStore | IDBIndex = options?.indexName
    ? store.index(options.indexName)
    : store;

  // Use cursor path for broad compatibility (Safari et al.)
  const result = await fetchCursor<T>(source, {
    query: options?.query ?? null,
    direction: options?.direction,
  });

  await transactionDone(tran);
  log(`transaction complete on ${storeName}`);
  return result;
}

/**
 * Deletes an item with the specified ID from an object store.
 *
 * @param conn - The database connection
 * @param storeName - Name of the object store
 * @param id - The ID of the item to delete
 * @returns Promise resolving when the item has been deleted
 */
export function del(
  conn: IDBDatabase,
  storeName: string,
  id: IDBValidKey
): Promise<void> {
  const tran = conn.transaction(storeName, 'readwrite');
  const store = tran.objectStore(storeName);
  const req = store.delete(id);

  return Promise.all([requestToPromise(req), transactionDone(tran)]).then(
    () => {
      log(`transaction complete on ${storeName}`);
    }
  );
}
